import { FormEvent, useEffect, useId, useState } from "react";

type ProjectFormProps = {
  initialName?: string;
  submitLabel: string;
  isSubmitting: boolean;
  onSubmit: (name: string) => Promise<void>;
  onCancel?: () => void;
};

const PROJECT_NAME_MAX_LENGTH = 120;

export function ProjectForm({
  initialName = "",
  submitLabel,
  isSubmitting,
  onSubmit,
  onCancel,
}: ProjectFormProps) {
  const inputId = useId();
  const [name, setName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(initialName);
  }, [initialName]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedName = name.trim();

    if (!trimmedName) {
      setError("Project name cannot be empty.");
      return;
    }

    if (trimmedName.length > PROJECT_NAME_MAX_LENGTH) {
      setError(`Use ${PROJECT_NAME_MAX_LENGTH} characters or fewer.`);
      return;
    }

    setError(null);
    await onSubmit(trimmedName);
    setName("");
  }

  return (
    <form className="space-y-3" onSubmit={handleSubmit}>
      <div>
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-slate-700"
        >
          Project name
        </label>
        <input
          id={inputId}
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={PROJECT_NAME_MAX_LENGTH}
          className="mt-2 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          placeholder="Event Guest Manager"
          disabled={isSubmitting}
        />
        <div className="mt-1 flex justify-between text-xs text-slate-500">
          <span>{error ?? "Duplicate names are allowed."}</span>
          <span>{name.trim().length}/{PROJECT_NAME_MAX_LENGTH}</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
        >
          {isSubmitting ? "Saving..." : submitLabel}
        </button>
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>
        ) : null}
      </div>
    </form>
  );
}
